// Members and pending invites of a tenant (docs/api.md 2.1, ADR-0024).
import { useState, type FormEvent } from "react";
import type { Invite, Member, Role, TenantDetail } from "../api/types";
import { useServices } from "../components/context";
import {
  PendingFilter,
  RestoreInstead,
  useGrace,
  type DeleteTarget,
  type GraceOps,
  type PendingRow,
} from "../components/Grace";
import { ErrorBanner, Section, useAction } from "../components/ui";
import { useDomains } from "../components/useDomains";
import { useLoad } from "../components/useLoad";
import { formatTime } from "../lib/format";
import { INDIE_INVITES_TEXT, inviteHint } from "../lib/plans";
import { atLeast } from "../lib/roles";

const ROLES: Role[] = ["reader", "publisher", "admin", "owner"];

/** Only an owner may add, remove or cancel the invite of an owner (ADR-0024 point 1). */
export function canManageRole(caller: Role, target: Role): boolean {
  return target === "owner" ? caller === "owner" : atLeast(caller, "admin");
}

/** A member as a grace object: the `user_id` is its ID in the grace routes. */
type MemberRow = Member & { id: string };

/** Grace routes of members and invites (registry 0.20.0): their own paths under `members/`. */
function memberOps(
  api: ReturnType<typeof useServices>["api"],
  tenant: string,
  allowed: boolean,
): GraceOps<MemberRow> {
  return {
    key: `members:${allowed}`,
    listPending: async () => ({
      items: allowed
        ? (await api.listPendingMembers(tenant)).items.map((m) => ({ ...m, id: m.user_id }))
        : [],
    }),
    remove: (t: DeleteTarget) => api.removeMember(tenant, t.id),
    restore: async (id) => void (await api.restoreMember(tenant, id)),
    purge: (id, confirm) => api.purgeMember(tenant, id, confirm),
  };
}

function inviteOps(
  api: ReturnType<typeof useServices>["api"],
  tenant: string,
  allowed: boolean,
): GraceOps<Invite> {
  return {
    key: `invites:${allowed}`,
    listPending: async () => ({ items: allowed ? (await api.listPendingMembers(tenant)).invites : [] }),
    remove: (t: DeleteTarget) => api.cancelInvite(tenant, t.id),
    restore: async (id) => void (await api.restoreInvite(tenant, id)),
    purge: (id, confirm) => api.purgeInvite(tenant, id, confirm),
  };
}

export function Members({ tenant }: { tenant: TenantDetail }) {
  const { api } = useServices();
  const allowed = atLeast(tenant.role, "admin");
  const domains = useDomains(tenant);
  const memberHint = inviteHint({ ...tenant, domains: domains.data ?? tenant.domains });
  const list = useLoad(
    () => (allowed ? api.listMembers(tenant.slug) : Promise.resolve(null)),
    [tenant.slug, allowed],
  );
  const action = useAction();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("reader");
  const [notice, setNotice] = useState<string | null>(null);
  const memberGrace = useGrace<MemberRow>({
    tenant,
    ops: memberOps(api, tenant.slug, allowed),
    onChange: list.reload,
  });
  const inviteGrace = useGrace<Invite>({
    tenant,
    ops: inviteOps(api, tenant.slug, allowed),
    onChange: list.reload,
  });

  if (!allowed) {
    return (
      <Section title="Members">
        <p className="muted">Only admins and owners can see and change members.</p>
      </Section>
    );
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setNotice(null);
    const ok = await action.run(async () => {
      const r = await api.addMember(tenant.slug, { email: email.trim().toLowerCase(), role });
      setNotice(
        r.pending
          ? `Invite stored for ${r.email}. It binds when this person signs in with a verified email.`
          : `${r.email} is now a member with role ${r.role}.`,
      );
    });
    if (ok) {
      setEmail("");
      list.reload();
    }
  }

  function onRemove(m: Member) {
    setNotice(null);
    const label = m.email ?? m.user_id;
    memberGrace.askDelete({
      id: m.user_id,
      title: `member ${label}`,
      message: "This person loses access to the tenant at once.",
    });
  }

  function onCancelInvite(i: Invite) {
    setNotice(null);
    inviteGrace.askDelete({
      id: i.id,
      title: `the invite for ${i.email}`,
      message: "The invite stops at once. It does not bind when this person signs in.",
    });
  }

  /** "Restore it instead" after an invite gives 409: the pending row is a member (`usr_`) or an invite (`inv_`). */
  function restoreInstead(p: { id: string }) {
    action.setError(null);
    const who = email.trim().toLowerCase();
    if (p.id.startsWith("inv_")) inviteGrace.restore(p.id, `the invite for ${who}`);
    else memberGrace.restore(p.id, `member ${who}`);
  }

  const memberRows: PendingRow[] = memberGrace.items.map((m) => {
    const label = m.email ?? m.user_id;
    return {
      id: m.user_id,
      title: label,
      detail: `Role ${m.role}`,
      deletedBy: m.deleted_by,
      deletedAt: m.deleted_at,
      purgeAfter: m.purge_after,
      canManage: canManageRole(tenant.role, m.role),
      purge: { id: m.user_id, title: `member ${label}`, confirm: label, confirmLabel: "email" },
    };
  });
  const inviteRows: PendingRow[] = inviteGrace.items.map((i) => ({
    id: i.id,
    title: i.email,
    detail: `Role ${i.role}`,
    deletedBy: i.deleted_by,
    deletedAt: i.deleted_at,
    purgeAfter: i.purge_after,
    canManage: canManageRole(tenant.role, i.role),
    purge: { id: i.id, title: `the invite for ${i.email}`, confirm: i.email, confirmLabel: "email" },
  }));

  const members = list.data?.items ?? [];
  const invites = list.data?.invites ?? [];

  return (
    <>
      <Section
        title="Members"
        actions={
          <PendingFilter view={memberGrace.view} setView={memberGrace.setView} count={memberGrace.count} />
        }
      >
        <p className="muted">Members are humans. Machines use principals.</p>
        <ErrorBanner error={list.error ?? memberGrace.error} />
        {notice && (
          <p className="banner ok" role="status">
            {notice}
          </p>
        )}
        {memberGrace.view === "pending" ? (
          memberGrace.rowsTable(memberRows)
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Since</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id}>
                  <td>{m.email ?? m.user_id}</td>
                  <td>
                    <span className="tag">{m.role}</span>
                  </td>
                  <td>{formatTime(m.created_at)}</td>
                  <td className="actions" data-label="Actions">
                    {canManageRole(tenant.role, m.role) && (
                      <button
                        type="button"
                        className="danger"
                        disabled={memberGrace.busy}
                        onClick={() => onRemove(m)}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      <Section
        title="Pending invites"
        actions={
          <PendingFilter view={inviteGrace.view} setView={inviteGrace.setView} count={inviteGrace.count} />
        }
      >
        <ErrorBanner error={inviteGrace.error} />
        {inviteGrace.view === "pending" ? (
          inviteGrace.rowsTable(inviteRows)
        ) : invites.length === 0 ? (
          <p className="muted">No pending invites.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Invited</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((i) => (
                <tr key={i.id}>
                  <td>{i.email}</td>
                  <td>
                    <span className="tag">{i.role}</span>
                  </td>
                  <td>{formatTime(i.created_at)}</td>
                  <td className="actions" data-label="Actions">
                    {canManageRole(tenant.role, i.role) && (
                      <button
                        type="button"
                        className="danger"
                        disabled={inviteGrace.busy}
                        onClick={() => onCancelInvite(i)}
                      >
                        Cancel invite
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      <Section title="Invite a member">
        {tenant.plan === "indie" ? (
          <p className="hint" role="note">
            {INDIE_INVITES_TEXT}
          </p>
        ) : (
          <form onSubmit={onInvite} className="form">
            {memberHint && (
              <p className="hint" role="note">
                {memberHint}
              </p>
            )}
            <label>
              Email
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label>
              Role
              <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                {ROLES.filter((r) => canManageRole(tenant.role, r)).map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <p className="muted">
              If the person has no account yet, Prego stores an invite. It binds when they sign in with this
              verified email.
            </p>
            <ErrorBanner error={action.error} />
            <RestoreInstead
              error={action.error}
              busy={memberGrace.busy || inviteGrace.busy}
              onRestore={restoreInstead}
            />
            <button type="submit" disabled={action.busy}>
              Invite
            </button>
          </form>
        )}
      </Section>
      {memberGrace.ui}
      {inviteGrace.ui}
    </>
  );
}
